import React, { useEffect, useRef, useState } from 'react';
import gsap from '../lib/gsap';
import heroVideo from '../assets/videos/hero.mp4';

interface HeroProps {
  content: any;
}

const Hero: React.FC<HeroProps> = ({ content }) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // Coming-soon mode: the hero is the whole page, so lock scrolling
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = [html.style.overflow, body.style.overflow, html.style.overscrollBehavior];
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    return () => {
      [html.style.overflow, body.style.overflow, html.style.overscrollBehavior] = prev;
    };
  }, []);

  useEffect(() => {
    // Animate hero entrance
    if (textRef.current) {
      gsap.fromTo(textRef.current,
        { opacity: 0, x: 100 },
        { 
          opacity: 1, 
          x: 0, 
          duration: 1.2, 
          ease: "power2.out",
          delay: 0.5
        }
      );
    }

    // Parallax effect on scroll - removed to prevent video from being cut off
    // if (heroRef.current) {
    //   gsap.to(heroRef.current, {
    //     yPercent: -50,
    //     ease: "none",
    //     scrollTrigger: {
    //       trigger: heroRef.current,
    //       start: "top bottom",
    //       end: "bottom top",
    //       scrub: true
    //     }
    //   });
    // }
  }, []);

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
    if (videoRef.current) {
      videoRef.current.play().catch(console.error);
    }
  };

  return (
    <section 
      ref={heroRef}
      className="relative w-full overflow-hidden flex items-center justify-center"
      style={{ height: '100dvh', minHeight: '100dvh' }}
      dir="rtl"
    >
      {/* Background Video */}
      <div className="absolute top-0 left-0 w-full h-full z-0">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={handleVideoLoad}
          poster="/api/placeholder/1920/1080"
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center'
          }}
        >
          <source
            src={(content.settings?.heroVideo && /^https?:\/\//.test(content.settings.heroVideo)) ? content.settings.heroVideo : heroVideo}
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>
        
        {/* Video Overlay */}
        <div className="absolute inset-0 bg-gradient-to-l from-bg-primary/90 via-bg-primary/60 to-transparent" />
      </div>

      {/* Fallback Background Image */}
      {!isVideoLoaded && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${content.settings?.backgroundImage || "/src/assets/backgrounds/3D Elements Grey .png"})`
          }}
        />
      )}

      {/* Coming-soon phrase overlay mid-right */}
      <div className="absolute inset-0 z-10 pointer-events-none flex items-center justify-end">
        <div className="font-madani text-white leading-tight text-right pr-8 sm:pr-12 md:pr-16 lg:pr-24" dir="ltr">
          <div className="text-5xl sm:text-6xl md:text-7xl font-black tracking-widest">SOON</div>
        </div>
      </div>

      {/* Hero Content removed as requested */}
      <div 
        ref={textRef}
        className="relative z-10 px-4 sm:px-8 md:px-16 lg:px-24 xl:px-32 w-full h-full flex items-center justify-center"
      />

      {/* Floating Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${4 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;
